import { h, ref, Teleport, Transition } from 'vue';

export type ToastType = 'success' | 'error';

/**
 * Shared state for the toast notification system.
 * Using singleton-like state outside the composable ensures that only one
 * toast is shown at a time even if multiple components use the composable.
 */
const visible = ref(false);
const message = ref('');
const type = ref<ToastType>('success');
let timeoutId: number | null = null;

/**
 * A lightweight toast notification system for the admin UI.
 * Provides a `showToast` method to trigger notifications and a `ToastComponent`
 * to be rendered in the component tree.
 */
export function useToast() {
  /**
   * Displays a toast message for a specified duration.
   * Subsequent calls will reset the timer and update the message.
   */
  const showToast = (msg: string, toastType: ToastType = 'success', duration = 1000) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    message.value = msg;
    type.value = toastType;
    visible.value = true;

    timeoutId = window.setTimeout(() => {
      visible.value = false;
      timeoutId = null;
    }, duration);
  };

  /**
   * Internal Toast component.
   */
  const ToastComponent = {
    setup() {
      return () =>
        h(Teleport, { to: 'body' }, [
          h(
            Transition,
            { name: 'toast-fade' },
            visible.value
              ? h(
                  'div',
                  {
                    class: ['toast-notification', `toast-${type.value}`],
                    id: 'toast-notification',
                    role: type.value === 'error' ? 'alert' : 'status',
                    'aria-live': type.value === 'error' ? 'assertive' : 'polite',
                  },
                  message.value,
                )
              : undefined,
          ),
        ]);
    },
  };

  return {
    showToast,
    ToastComponent,
  };
}
